import React, { FC, useMemo } from 'react';
import { useLocale } from 'src/modules/locale';
import styles from './SearchSuggestions.module.scss';
import { ProductCategoryEntity, ProductEntity, ProductLocalizedEntity } from 'src/modules/products';
import { getProductName } from 'src/modules/products/utils/products';
import Link from 'next/link';
import { ProductCategoryLocalizedEntity } from 'src/modules/products/types';
import { MAX_SUGGESTED_CATEGORIES } from 'src/modules/search/components/constants';
import ScrollBound from 'react-scroll-bound';
import { FormattedMessage } from 'react-intl';

export interface SearchSuggestionsProps {
  suggestions: ProductEntity[];
  onClose: () => void;
}

export const SearchSuggestions: FC<SearchSuggestionsProps> = ({ suggestions, onClose }) => {
  const { getLocalizedObject } = useLocale();

  const suggestedCategories = useMemo(() => {
    const categories: ProductCategoryEntity[] = [];

    suggestions.forEach((suggestion) => {
      if (categories.length < MAX_SUGGESTED_CATEGORIES && suggestion.product_categories) {
        suggestion.product_categories.forEach((category) => {
          if (!categories.find((c) => c.id === category.id)) {
            categories.push(category);
          }
        });
      }
    });

    return categories;
  }, [suggestions]);

  return (
    <div className={styles.container}>
      <ScrollBound className={styles.suggestionsContainer} tagName="div">
        {suggestions.map((s) => {
          if (!s.product_categories) {
            return null;
          }

          const localizedSuggestion = getLocalizedObject<ProductLocalizedEntity>(s);
          const imageThumbnailStyle = s.assets?.[0]
            ? { backgroundImage: `url(${s.assets?.[0]?.file?.formats?.thumbnail?.url})` }
            : {};
          return (
            <div key={localizedSuggestion.id} className={styles.suggestionContainer}>
              <Link
                href="/products/[category]/[slug]"
                as={`/products/${localizedSuggestion.product_categories[0]?.slug}/${localizedSuggestion.slug}`}>
                <a className={styles.suggestion} onClick={onClose}>
                  <div className={styles.suggestionThumbnail} style={imageThumbnailStyle} />
                  <h4 className={styles.suggestionTitle}>{getProductName(localizedSuggestion)}</h4>
                </a>
              </Link>
            </div>
          );
        })}
      </ScrollBound>
      <div className={styles.suggestedCategoriesContainer}>
        {suggestedCategories.map((c) => {
          const localizedCategory = getLocalizedObject<ProductCategoryLocalizedEntity>(c);
          return (
            <Link
              key={localizedCategory.id}
              href="/products/[category]"
              as={`/products/${localizedCategory.slug}`}>
              <a className={styles.suggestedCategory}>
                <h4>
                  <FormattedMessage
                    id="search/all_category_label"
                    values={{ category: localizedCategory.name }}
                  />
                </h4>
              </a>
            </Link>
          );
        })}
      </div>
    </div>
  );
};
